import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  Button
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import LabelIcon from '@mui/icons-material/LabelOutlined';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const stringify = (value) => {
  if (!value) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const SemanticTableViewDialog = ({
  open,
  onClose,
  record,
  database
}) => {
  const tags = useMemo(() => (Array.isArray(record?.tags) ? record.tags : []), [record]);
  const columns = useMemo(() => (Array.isArray(record?.columns) ? record.columns : []), [record]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
        Detalles de la entidad semántica
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  bgcolor: 'var(--wb-x-bg-6366f133, rgba(99, 102, 241, 0.2))',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <TableChartIcon sx={{ color: 'var(--wb-x-text-818cf8, #818cf8)' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {record?.display_name || record?.table_name || 'Sin nombre'}
                </Typography>
                <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mt: 0.5 }}>
                  {record?.description || 'Sin descripción registrada.'}
                </Typography>
              </Box>
            </Stack>
            {tags.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    icon={<LabelIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      backgroundColor: 'var(--wb-x-bg-3b82f626, rgba(59, 130, 246, 0.15))',
                      color: 'var(--wb-x-text-bfdbfe, #bfdbfe)',
                      '& .MuiChip-icon': { color: 'var(--wb-blue-light)' }
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Identificadores
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Table ID: {record?.table_id || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Semantic DB: {record?.semantic_database_id || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Cuenta: {record?.account_id || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Conexión: {record?.connection_id || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Catálogo y estado
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Catálogo: {record?.catalog || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Base · Esquema: {(record?.database || '—') + ' · ' + (record?.schema || '—')}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Dialecto SQL: {record?.sql_dialect || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Versión: {record?.version || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Estado: {record?.soft_delete?.deleted ? 'Eliminada' : record?.is_active === false ? 'Inactiva' : 'Activa'}
              </Typography>
              {record?.soft_delete?.deleted && (
                <Typography variant="body2" color="var(--wb-x-text-f87171d9, rgba(248, 113, 113, 0.85))">
                  Eliminada el {formatDate(record?.soft_delete?.deleted_at)}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1 }}>
              Base semántica asociada
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <StorageIcon sx={{ color: 'var(--wb-muted)' }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {database?.name || database?.display_name || database?.semantic_database_id || '—'}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1 }}>
              Columnas ({columns.length})
            </Typography>
            {columns.length === 0 ? (
              <Typography variant="body2" color="var(--wb-text-soft)">
                No se registraron columnas.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {columns.map((column) => (
                  <Box
                    key={column?.column_id || column?.column_name}
                    sx={{
                      bgcolor: 'var(--wb-x-bg-94a3b814, rgba(148, 163, 184, 0.08))',
                      borderRadius: 1.5,
                      p: 1.5
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-title)' }}>
                      {column?.column_name || 'Sin nombre'}
                    </Typography>
                    <Typography variant="body2" color="var(--wb-text-soft)">
                      {column?.description || 'Sin descripción'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      {column?.business_name && (
                        <Chip
                          size="small"
                          label={`Business: ${column.business_name}`}
                          sx={{ bgcolor: 'var(--wb-accent-18)', color: 'var(--wb-x-text-bae6fd, #bae6fd)' }}
                        />
                      )}
                      {column?.data_type && (
                        <Chip
                          size="small"
                          label={`Tipo: ${column.data_type}`}
                          sx={{ bgcolor: 'var(--wb-x-bg-86efac2e, rgba(134, 239, 172, 0.18))', color: 'var(--wb-x-text-bbf7d0, #bbf7d0)' }}
                        />
                      )}
                      {column?.semantic_role && (
                        <Chip
                          size="small"
                          label={`Rol: ${column.semantic_role}`}
                          sx={{ bgcolor: 'var(--wb-x-bg-f472b62e, rgba(244, 114, 182, 0.18))', color: 'var(--wb-x-text-fbcfe8, #fbcfe8)' }}
                        />
                      )}
                      {column?.semantic_type && (
                        <Chip
                          size="small"
                          label={`Tipo semántico: ${column.semantic_type}`}
                          sx={{ bgcolor: 'var(--wb-x-bg-facc152e, rgba(250, 204, 21, 0.18))', color: 'var(--wb-x-text-facc15, #facc15)' }}
                        />
                      )}
                      {column?.is_primary_key && (
                        <Chip
                          size="small"
                          label="Llave primaria"
                          sx={{ bgcolor: 'var(--wb-x-bg-6366f12e, rgba(99, 102, 241, 0.18))', color: 'var(--wb-x-text-c7d2fe, #c7d2fe)' }}
                        />
                      )}
                      {column?.required && (
                        <Chip
                          size="small"
                          label="Obligatoria"
                          sx={{ bgcolor: 'var(--wb-x-bg-ef44442e, rgba(239, 68, 68, 0.18))', color: 'var(--wb-danger-light)' }}
                        />
                      )}
                      {column?.sensitive && (
                        <Chip
                          size="small"
                          label="Dato sensible"
                          sx={{ bgcolor: 'var(--wb-x-bg-fbbf242e, rgba(251, 191, 36, 0.18))', color: 'var(--wb-x-text-fcd34d, #fcd34d)' }}
                        />
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Divider sx={{ borderColor: 'var(--wb-overlay-08)' }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Lineage
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  borderRadius: 1,
                  p: 2,
                  color: 'var(--wb-text)',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {stringify(record?.lineage)}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Governance
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  borderRadius: 1,
                  p: 2,
                  color: 'var(--wb-text)',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {stringify(record?.governance)}
              </Box>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
              Reglas de calidad
            </Typography>
            {Array.isArray(record?.quality_rules) && record.quality_rules.length > 0 ? (
              <Stack spacing={1.5}>
                {record.quality_rules.map((rule) => (
                  <Box
                    key={rule?.name || rule?.rule || JSON.stringify(rule)}
                    sx={{
                      bgcolor: 'var(--wb-x-bg-37415199, rgba(55, 65, 81, 0.6))',
                      borderRadius: 1,
                      p: 1.5
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-title)' }}>
                      {rule?.name || rule?.rule || 'Regla sin nombre'}
                    </Typography>
                    <Typography variant="body2" color="var(--wb-text-soft)">
                      {rule?.description || 'Sin descripción'}
                    </Typography>
                    {rule?.severity && (
                      <Chip
                        size="small"
                        label={`Severidad: ${rule.severity}`}
                        sx={{ mt: 1, bgcolor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}
                      />
                    )}
                    {rule?.expression && (
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'var(--wb-bg-overlay-60)',
                          borderRadius: 1,
                          p: 1,
                          mt: 1,
                          color: 'var(--wb-text)',
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {rule.expression}
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="var(--wb-text-soft)">
                No se registraron reglas de calidad.
              </Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Auditoría
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Creado por: {record?.audit?.created_by || '—'}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Actualizado por: {record?.audit?.updated_by || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Fechas
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Creado el: {formatDate(record?.audit?.created_at)}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                Actualizado el: {formatDate(record?.audit?.updated_at)}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)' }}>
        <Button onClick={onClose} sx={{ color: 'var(--wb-text-label)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SemanticTableViewDialog;
